// Models
import Model from './Model'
import Account from './Account'

// Constants
import { PROFILE_STATUSES } from './Profile'
import { SUBSCRIPTION_PLUS } from '@/models/Subscription'

class AccountProfile extends Model {
  get fullAddress () {
    return [
      this.address,
      this.city,
      this.zip
    ].filter(Boolean).join(', ')
  }

  get isPrivate () {
    return this.profileType === PROFILE_TYPES.PRIVATE
  }

  get isLegal () {
    return this.profileType === PROFILE_TYPES.LEGAL
  }

  get isBlocked () {
    return [PROFILE_STATUSES.BLOCKED, PROFILE_STATUSES.FREEZE].includes(this.status)
  }

  get isClosed () {
    return this.status === PROFILE_STATUSES.CLOSED
  }

  get hasEmiTier () {
    return this.tiers.some((tier) => tier === TIERS.EMI)
  }

  get hasProfilePicture () {
    return this.pictureImageUrl != null
  }

  get hasVipStatus () {
    return this.vip === true
  }

  get hasPremiumSubscription () {
    return this.subscription === SUBSCRIPTION_PLUS
  }

  get isMultiSignatureEnabled () {
    return this.signaturesRequired > 1
  }

  isFeatureEnabled (feature) {
    return this.features?.some((featureItem) => featureItem.feature === feature && featureItem.enabled) ?? false
  }

  static get tierOptions () {
    return [
      { name: 'Affiliate', value: TIERS.AFFILIATE },
      { name: 'Private I', value: TIERS.PRIVATE1 },
      { name: 'Private II', value: TIERS.PRIVATE2 },
      { name: 'Simplified Onboarding', value: TIERS.SIMPLIFIED_ONBOARDING },
      { name: 'Business', value: TIERS.BUSINESS },
      { name: 'Crypto', value: TIERS.CRYPTO },
      { name: 'Legal Gambling', value: TIERS.LEGAL_GAMBLING },
      { name: 'IT/Marketing/Consulting', value: TIERS.IT_MARKETING_CONSULTING },
      { name: 'EMI', value: TIERS.EMI },
      { name: 'Administrative', value: TIERS.ADMINISTRATIVE },
      { name: 'Manual', value: TIERS.MANUAL },
      { name: 'Manual Lite', value: TIERS.MANUAL_LITE },
      { name: 'Unclassified', value: TIERS.UNCLASSIFIED },
      { name: 'Bilderlings Group', value: TIERS.BILDERLINGS_GROUP },
      { name: 'BO100', value: TIERS.BO100 },
      { name: 'BOL100', value: TIERS.BOL100 },
      { name: 'No Permit', value: TIERS.NO_PERMIT },
      { name: 'Manual In', value: TIERS.MANUAL_IN },
      { name: 'Manual Out', value: TIERS.MANUAL_OUT },
      { name: 'Private', value: TIERS.PRIVATE },
      { name: 'Not yet defined', value: TIERS.UNDEFINED }
    ]
  }

  static get riskTypeOptions () {
    return [
      { name: 'High', value: RISK_TYPES.HIGH },
      { name: 'Medium', value: RISK_TYPES.MEDIUM },
      { name: 'Low', value: RISK_TYPES.LOW }
    ]
  }

  static get statusOptions () {
    return [
      { name: 'Reserved', value: PROFILE_STATUSES.RESERVED },
      { name: 'Active', value: PROFILE_STATUSES.ACTIVE },
      { name: 'Blocked', value: PROFILE_STATUSES.BLOCKED },
      { name: 'Freeze', value: PROFILE_STATUSES.FREEZE },
      { name: 'Closure', value: PROFILE_STATUSES.CLOSURE },
      { name: 'Trial', value: PROFILE_STATUSES.TRIAL },
      { name: 'Closed', value: PROFILE_STATUSES.CLOSED }
    ]
  }

  static get smsNotificationOptions () {
    return [
      { name: 'Top-up', value: SMS_NOTIFICATIONS.TOPUP }
    ]
  }

  static get emailNotificationOptions () {
    return [
      { name: 'Top-up', value: EMAIL_NOTIFICATIONS.TOPUP }
    ]
  }

  static get typeOptions () {
    return [
      { name: 'Legal', value: PROFILE_TYPES.LEGAL },
      { name: 'Private', value: PROFILE_TYPES.PRIVATE }
    ]
  }

  static get licenseOptions () {
    return [
      { name: 'Great Britain', value: PROFILE_LICENSES.GB },
      { name: 'United States', value: PROFILE_LICENSES.US }
    ]
  }

  get formatters () {
    return {
      accounts: dataArray => dataArray.map(data => new Account(data))
    }
  }

  get schema () {
    return [
      'id',
      'salesforceId',
      'profileType',
      'name',
      'legalName',
      'firstName',
      'lastName',
      'email',
      'address',
      'city',
      'zip',
      'pictureImageUrl',
      'status',
      'regNumber',
      'country',
      'allowedSmsNotifications',
      'allowedEmailNotifications',
      'accounts',
      'feeGroups',
      'useProfileFeeWallet',
      'onboarderId',
      'onboarderName',
      'seller',
      'agent',
      'residence',
      'region',
      'createdAt',
      'updatedAt',
      'tiers',
      'tierComment',
      'canCreateWallet',
      'canCreateAccount',
      'maxAccounts',
      'vip',
      'turnoverIncoming',
      'turnoverOutgoing',
      'partnerOneTimeComission',
      'partnerRegularComission',
      'voicePassword',
      'promotion',
      'employer',
      'userIds',
      'riskType',
      'businessProfile',
      'statusChangeReason',
      'signaturesRequired',
      'signaturesRequiredAmountFrom',
      'features',
      'license',
      'subscription',
      'dormant'
    ]
  }
}

export const PROFILE_TYPES = {
  PRIVATE: 'PRIVATE',
  LEGAL: 'LEGAL',
  REMITTANCE: 'REMITTANCE'
}

export const TIERS = {
  AFFILIATE: 'affiliate',
  PRIVATE1: 'private1',
  PRIVATE2: 'private2',
  SIMPLIFIED_ONBOARDING: 'simplifiedOnboarding',
  BUSINESS: 'business',
  CRYPTO: 'crypto',
  LEGAL_GAMBLING: 'legalGambling',
  IT_MARKETING_CONSULTING: 'itMarketingConsulting',
  EMI: 'emi',
  UNCLASSIFIED: 'unclassified',
  BILDERLINGS_GROUP: 'bilderlingsGroup',
  ADMINISTRATIVE: 'administrative',
  MANUAL: 'manual',
  MANUAL_LITE: 'manual-lite',
  BO100: 'bo100',
  BOL100: 'bol100',
  NO_PERMIT: 'noPermit',
  UNDEFINED: 'undefined',
  MANUAL_IN: 'manual-in',
  MANUAL_OUT: 'manual-out',
  PRIVATE: 'private'
}

export const RISK_TYPES = {
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW'
}

export const SMS_NOTIFICATIONS = {
  TOPUP: 'TOPUP'
}

export const EMAIL_NOTIFICATIONS = {
  TOPUP: 'TOPUP'
}

export const PROFILE_FEATURES = {
  OPEN_BANKING_INTEGRATION: 'open-banking-integration'
}

export const PROFILE_LICENSES = {
  GB: 'GB',
  US: 'US'
}

export { PROFILE_STATUSES }

export default AccountProfile
